import { Component } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { GraphQLError } from 'graphql';
import { CurrentUserQuery, AccountsGQL, AccountFieldsFragment, AccountsQuery, AccountsExportGQL, AccountFilterContainer } from 'src/app/graphql/gql';
import { ColumnDefinition, PaginatedTableComponent } from 'src/app/paginated-table/paginated-table.component';
import { DataMapperResult } from 'src/app/paginated-table/table-data-source';
import { FILTER_DEFINITIONS } from './accounts-filter-defs';
import { MenuItem } from 'primeng/api';
import { ViewChild } from '@angular/core';
import { CreateAccountComponent } from '../create-account/create-account.component';
import { EditAccountComponent } from '../edit-account/edit-account.component';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { DialogModule as PDialogModule } from 'primeng/dialog';
import { PaginatedTableModule } from 'src/app/paginated-table/paginated-table.module';

@Component({
  selector: 'app-accounts',
  templateUrl: './accounts.component.html',
  styleUrls: ['./accounts.component.css'],
  imports: [
    ButtonModule,
    CreateAccountComponent,
    EditAccountComponent,
    PaginatedTableModule,
    PDialogModule,
    ToolbarModule
  ]
})
export class AccountsComponent {
  @ViewChild('accountsTable') table!: PaginatedTableComponent<any, any, any, any>;

  loading = true;
  data!: CurrentUserQuery;
  errors: readonly GraphQLError[] | undefined;
  selection: AccountFieldsFragment[] = []
  selectAll = false
  filterQueryParameter: AccountFilterContainer | null = null
  accountFormVisible = false;
  newAccountFormVisible = false;
  creatingAccount = false;
  selectedAccount?: AccountFieldsFragment;

  rowMenuItems: MenuItem[] = [
    {
      label: $localize`:menu|header:Action`,
      items: [
        { label: $localize`:menu|action:Edit`, icon: 'pi pi-pencil', command: () => { this.editAccount(); } },
      ]
    }
  ];

  columns: ColumnDefinition[] = [
    {
      attr: 'clientNumber',
      label: $localize`:accounts|Client number column:Client Number`,
      orderKey: 'client_number'
    },
    {
      attr: 'company',
      label: $localize`:accounts|Company column:Company`,
      orderKey: 'company'
    },
    {
      attr: 'state',
      label: $localize`:accounts|State column:State`,
      orderKey: 'state'
    },
    {
      attr: 'street',
      label: $localize`:accounts|Street column:Street`,
      visible: false,
      orderKey: 'street'
    },
    {
      attr: 'zip',
      label: $localize`:accounts|ZIP column:ZIP`,
      visible: false,
      orderKey: 'zip'
    },
    {
      attr: 'city',
      label: $localize`:accounts|City column:City`,
      visible: false,
      orderKey: 'city'
    },
    {
      attr: 'country',
      label: $localize`:accounts|Country column:Country`,
      orderKey: 'country'
    },
    {
      attr: 'country_state',
      label: $localize`:accounts|Country state column:Country State`,
      visible: false
    },
    {
      attr: 'phone',
      label: $localize`:accounts|Phone column:Phone`,
      visible: false,
      orderKey: 'phone'
    }
  ]
  filters = FILTER_DEFINITIONS

  constructor(
    private apollo: Apollo,
    public accountsGQL: AccountsGQL,
    public accountsExport: AccountsExportGQL
  ) {}

  dataMapper(data: AccountsQuery): DataMapperResult<AccountFieldsFragment> {
    return {
      items: data.accounts!.edges!.map(edge => edge!.node!),
      totalCount: data.accounts!.totalCount || null
    }
  }

  onSelectionChange(selection: AccountFieldsFragment[]) {
    this.selection = selection
  }

  onSelectAllChange(selectAll: boolean) {
    this.selectAll = selectAll
  }

  onFilterQueryParameterChange(filterQueryParameter: AccountFilterContainer | null) {
    this.filterQueryParameter = filterQueryParameter
  }

  editAccount() {
    this.selectedAccount = this.table.currentRowMenuItem;
    this.accountFormVisible = true;
  }

  onAccountEditSuccess = () => {
    this.accountFormVisible = false;
    this.table?.loadData(false)
  }

  onCreateAccountSuccess = () => {
    this.newAccountFormVisible = false
    this.table?.loadData(true)
  }

  onExportClick() {
    this.accountsExport.fetch({
      ids: (this.selectAll || this.selection.length == 0) ? null : this.selection.map(o => o.id),
      filter: this.filterQueryParameter
    }).subscribe({
      next: (response) => {
        const csvData = response.data.accountsExport
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'accounts-export-' + new Date().toISOString() + '.csv'
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      }
    });
  }

  onCreateNewAccount() {
    this.selectedAccount = this.table.currentRowMenuItem;
    this.newAccountFormVisible = true;
  }
}
